import React from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './MarkdownRenderer.css';

// Professional markdown renderer using marked and DOMPurify
const MarkdownRenderer = ({ text, className = '' }) => {
    if (!text) {
        return null;
    }
    
    try {
        // Preprocess text to fix markdown formatting issues
        let processedText = text;
        
        // Normalize list patterns
        processedText = processedText.replace(/^(\s*)\*\s{2,}/gm, '$1* ');
        processedText = processedText.replace(/^(\s*)-\s{2,}(?!-)/gm, '$1- ');
        
        // Handle list markers embedded in text
        processedText = processedText.replace(/([^\n])\s+([*-])\s{2,}(\*\*[^*]+\*\*)/g, '$1\n$2 $3');
        processedText = processedText.replace(/([^\n])\s+([*-])\s+(\*\*[^*]+\*\*)/g, '$1\n$2 $3');
        processedText = processedText.replace(/([^\n])\s+(\*\*\d+\.\s[^*]+?\*\*)/g, '$1\n* $2');
        processedText = processedText.replace(/^(\s*)(\*\*\d+\.\s[^*]+?\*\*)/gm, '$1* $2');
        
        // Fix list markers after newlines or periods
        processedText = processedText.replace(/(\n|\.)\s*\*\s{2,}/g, '$1\n* ');
        processedText = processedText.replace(/(\n|\.)\s*-\s{2,}(?!-)/g, '$1\n- ');
        processedText = processedText.replace(/(\n|\.)\s*\*\s+/g, '$1\n* ');
        processedText = processedText.replace(/(\n|\.)\s*-\s+(?!-)/g, '$1\n- ');
        
        // Fix list markers with no space
        processedText = processedText.replace(/^(\s*)\*(?![*\s])(.+)/gm, '$1* $2');
        processedText = processedText.replace(/^(\s*)-(?![-\s])(.+)/gm, '$1- $2');
        
        // Handle numbered lists
        processedText = processedText.replace(/^(\s*)[*-]\s+(\d+\.\s)/gm, '$1$2');
        processedText = processedText.replace(/^(\s*)(\d+)\.\s{2,}/gm, '$1$2. ');
        
        // Split into lines for additional processing
        const lines = processedText.split('\n');
        const processedLines = [];
        
        for (let i = 0; i < lines.length; i++) {
            let line = lines[i];
            
            // Ensure list items have proper spacing
            if (/^\s*\*\s+\*\*/.test(line)) {
                line = line.replace(/^(\s*)\*\s+(\*\*)/, '$1* $2');
            }
            if (/^\s*-\s+\*\*/.test(line)) {
                line = line.replace(/^(\s*)-\s+(\*\*)/, '$1- $2');
            }
            
            // Fix inconsistent spacing
            if (/^\s*\*/.test(line) && !/^\s*\*\s/.test(line) && !/^\s*\*\*/.test(line)) {
                line = line.replace(/^(\s*)\*([^\s*])/, '$1* $2');
            }
            if (/^\s*-/.test(line) && !/^\s*-\s/.test(line) && !/^\s*--/.test(line)) {
                line = line.replace(/^(\s*)-([^\s-])/, '$1- $2');
            }
            
            processedLines.push(line);
        }
        
        processedText = processedLines.join('\n');
        
        // Final normalization
        processedText = processedText.replace(/^(\s*)\*\s{2,}/gm, '$1* ');
        processedText = processedText.replace(/^(\s*)-\s{2,}(?!-)/gm, '$1- ');
        
        // Configure marked for clean output
        const markedOptions = {
            breaks: true,
            gfm: true,
            headerIds: false,
            mangle: false,
            pedantic: false,
            smartLists: true,
            smartypants: false
        };
        
        marked.setOptions(markedOptions);
        
        // Convert markdown to HTML
        const rawMarkup = marked(processedText);
        
        // Sanitize HTML to prevent XSS
        const cleanMarkup = DOMPurify.sanitize(rawMarkup, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a'],
            ALLOWED_ATTR: ['href', 'target', 'rel']
        });
        
        return (
            <div 
                className={`markdown-content ${className}`}
                dangerouslySetInnerHTML={{ __html: cleanMarkup }}
            />
        );
    } catch (error) {
        console.warn('Markdown rendering error, falling back to plain text:', error);
        // Fallback to plain text with basic formatting
        return (
            <div className={`markdown-content ${className}`}>
                {text.split('\n').map((line, i) => (
                    <p key={i}>{line || '\u00A0'}</p>
                ))}
            </div>
        );
    }
};

export default MarkdownRenderer;
